// --- src/components/Dashboard.tsx ---
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { AppView, SupplierMap, InventoryMap, Permissions } from './types';
import {
  Search,
  Sparkles,
  AlertCircle,
  CheckCircle,
  PackageX,
  Trash2,
  Pencil,
  X,
  Save,
  Printer,
  Loader2,
  ShoppingCart,
  AlertTriangle,
  Clock,
  Activity,
  Bell,
  Lock,
} from 'lucide-react';
import { ConfirmDialog } from './ConfirmDialog';
import { getUserFriendlyError } from '../utils/errorHelper';
import { ErrorDisplay } from './ErrorDisplay';
import { toast } from 'react-hot-toast';
import { supabaseService } from '../services/supabaseService';
import { InventoryGlobalStats } from '../services/inventoryService';
import { exportShoppingListToExcel } from '../modules/procurement/procurementUtils';
import { PrintableShoppingList } from '../modules/procurement/PrintableShoppingList';
import { PricingInput } from './PricingInput';

interface DashboardProps {
  setView: (view: AppView) => void;
  supplierData: SupplierMap;
  inventoryData: InventoryMap;
  setInventoryData?: React.Dispatch<React.SetStateAction<InventoryMap>>;
  onDelete: (ean: string) => void;
  onUpdate: (
    ean: string,
    data: {
      name: string;
      price: number; // Gross purchase price
      selling_price?: number; // Gross selling price
      netPrice?: number; // Net purchase price
      netSellingPrice?: number; // Net selling price
      vatRate?: number; // VAT rate
      quantity: number;
      unit?: 'db' | 'm' | 'kg';
      is_weighable?: boolean;
      unit_weight?: number;
      min_quantity?: number | null;
    }
  ) => void;
  addToPrintQueue?: (ean: string) => void;
  currentPage: number;
  totalItems: number;
  goToNextPage: () => void;
  goToPrevPage: () => void;
  pageSize: number;
  isAdmin?: boolean;
  permissions?: Permissions | null;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  filterMode: 'ALL' | 'LOW_STOCK' | 'SHOPPING_LIST' | 'NO_PRICE';
  setFilterMode: (mode: 'ALL' | 'LOW_STOCK' | 'SHOPPING_LIST' | 'NO_PRICE') => void;
  fetchAllItemsForPrint?: () => Promise<{ supplierMap: SupplierMap; inventoryMap: InventoryMap }>;
}

const Dashboard: React.FC<DashboardProps> = ({
  setView,
  supplierData,
  inventoryData,
  setInventoryData,
  onDelete,
  onUpdate,
  addToPrintQueue,
  currentPage,
  totalItems,
  goToNextPage,
  goToPrevPage,
  pageSize,
  isAdmin = false,
  permissions,
  searchTerm,
  setSearchTerm,
  filterMode,
  setFilterMode,
  fetchAllItemsForPrint,
}) => {
  // Check if procurement module is enabled
  const hasProcurementModule = permissions?.hasProcurementModule ?? true; // Default to true for backward compatibility

  // Full list for print (when totalItems > pageSize) – populated before window.print()
  const [printFullData, setPrintFullData] = useState<{
    supplierMap: SupplierMap;
    inventoryMap: InventoryMap;
  } | null>(null);
  const [printingAll, setPrintingAll] = useState(false);

  // Global statistics from server (fixes pagination issues)
  const [globalStats, setGlobalStats] = useState<InventoryGlobalStats | null>(null);

  // Bulk selection state for shopping list
  const [selectedEans, setSelectedEans] = useState<string[]>([]);

  // Clear selection when filter changes
  useEffect(() => {
    setSelectedEans([]);
  }, [filterMode]);

  // Fetch global statistics from server
  useEffect(() => {
    const loadGlobalStats = async () => {
      try {
        await supabaseService.getInventoryStats().then(setGlobalStats);
      } catch (error) {
        console.error('Error loading global stats:', error);
        // Keep globalStats as null to show loading state
      }
    };

    loadGlobalStats();
  }, [inventoryData]); // Refresh when inventoryData changes

  // Szerkesztés állapota (Melyik EAN-t szerkesztjük éppen? Ha null, akkor nincs megnyitva ablak)
  const [editingItem, setEditingItem] = useState<{
    ean: string;
    name: string;
    price: number; // Gross purchase price (legacy)
    sellingPrice: number; // Gross selling price (legacy)
    // New hybrid pricing fields
    netPrice?: number;
    netSellingPrice?: number;
    vatRate?: number;
    quantity: number;
    unit: 'db' | 'm' | 'kg';
    is_weighable: boolean;
    unit_weight: number;
    min_quantity: number | null;
  } | null>(null);

  // Confirmation dialog state
  const [deleteConfirm, setDeleteConfirm] = useState<{ ean: string } | null>(null);

  // Loading states
  const [isDeleting, setIsDeleting] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState<string | null>(null);
  const [togglingShoppingList, setTogglingShoppingList] = useState<string | null>(null);

  // Error state
  const [error, setError] = useState<{
    error: any;
    context: { action: string; item?: string };
  } | null>(null);

  // Recent activity state
  const [recentActivity, setRecentActivity] = useState<
    Array<{
      id: string;
      action_type: string;
      details: string;
      created_at: string;
      user_name: string;
    }>
  >([]);
  const [loadingActivity, setLoadingActivity] = useState(false);

  // Load recent activity on mount and when data changes
  useEffect(() => {
    loadRecentActivity();
  }, []);

  const loadRecentActivity = async () => {
    setLoadingActivity(true);
    try {
      const activity = await supabaseService.getRecentActivity(5);
      setRecentActivity(activity);
    } catch (error) {
      console.error('Error loading recent activity:', error);
    } finally {
      setLoadingActivity(false);
    }
  };

  // Inject print styles on mount
  useEffect(() => {
    const styleId = 'dashboard-print-styles';
    if (!document.getElementById(styleId)) {
      const styleSheet = document.createElement('style');
      styleSheet.id = styleId;
      styleSheet.textContent = `
        @media print {
          @page {
            size: A4;
            margin: 1cm;
          }
          
          * {
            print-color-adjust: exact !important;
            -webkit-print-color-adjust: exact !important;
          }
          
          body {
            background: white !important;
            color: black !important;
          }
          
          /* Hide everything except print content */
          nav,
          header,
          button,
          .print\\:hidden,
          [class*="navbar"],
          [class*="sidebar"],
          [class*="stats"],
          [class*="filter"],
          [class*="pagination"],
          [class*="search"],
          [class*="action"],
          [class*="card"]:not(.print-table-container) {
            display: none !important;
          }
          
          /* Show print-only elements */
          .print\\:block {
            display: block !important;
          }
          
          .print\\:table-cell {
            display: table-cell !important;
          }
          
          /* Force white background and black text */
          .print-table-container,
          .print-table-container * {
            background: white !important;
            color: black !important;
            box-shadow: none !important;
          }
          
          /* Table styling for print */
          .print-table-container table {
            width: 100% !important;
            border-collapse: collapse !important;
            font-size: 10pt !important;
            background: white !important;
          }
          
          .print-table-container th,
          .print-table-container td {
            padding: 8px !important;
            border: 1px solid #000 !important;
            text-align: left !important;
            background: white !important;
            color: black !important;
          }
          
          .print-table-container th {
            background-color: #f5f5f5 !important;
            font-weight: bold !important;
            color: black !important;
          }
          
          .print-table-container tr {
            page-break-inside: avoid !important;
            background: white !important;
          }
          
          /* Ensure header is visible */
          .print-table-container h1 {
            margin-bottom: 1cm !important;
            font-size: 18pt !important;
            color: black !important;
            background: white !important;
          }
          
          /* Hide dark mode styles */
          [class*="dark:"] {
            color: black !important;
            background: white !important;
          }
        }
      `;
      document.head.appendChild(styleSheet);
    }
  }, []);

  // Build table rows from supplier + inventory maps (shared for table and full-list print)
  const buildTableRows = useCallback(
    (
      supplierMap: SupplierMap,
      inventoryMap: InventoryMap,
      mode: 'ALL' | 'LOW_STOCK' | 'SHOPPING_LIST' | 'NO_PRICE'
    ) => {
      const allEans = new Set([...Object.keys(supplierMap), ...Object.keys(inventoryMap)]);
      const rows = Array.from(allEans).map((ean) => {
        const suppItem = supplierMap[ean];
        const invItem = inventoryMap[ean];
        const name = suppItem?.name || 'Ismeretlen Termék';
        const netPrice = suppItem?.netPrice || 0;
        const vatRate = suppItem?.vatRate || 27;
        const grossPurchasePrice =
          netPrice > 0 ? Math.round(netPrice * (1 + vatRate / 100) * 100) / 100 : 0;
        const sellingPrice = suppItem?.sellingPrice || 0;
        const quantity = invItem?.quantity || 0;
        const minQuantity = invItem?.min_quantity;
        const onShoppingList = invItem?.on_shopping_list ?? false;
        const total = netPrice * quantity;
        const scannedAt = invItem?.scannedAt;
        // NULL = no limit, 0 = valid limit
        const isLowStock = minQuantity != null && quantity <= minQuantity;
        const isLowStockUnhandled = isLowStock && !onShoppingList;
        let status: 'ok' | 'missing' | 'unknown' = 'missing';
        if (invItem) status = suppItem ? 'ok' : 'unknown';
        else status = 'missing';
        const unit = invItem?.unit || suppItem?.unit || 'db';
        return {
          ean,
          name,
          price: netPrice,
          grossPurchasePrice,
          sellingPrice,
          quantity,
          total,
          status,
          scannedAt,
          minQuantity: minQuantity ?? null,
          onShoppingList,
          isLowStock,
          isLowStockUnhandled,
          unit,
        };
      });
      let filtered = rows;
      if (mode === 'LOW_STOCK') filtered = filtered.filter((row) => row.isLowStockUnhandled);
      else if (mode === 'SHOPPING_LIST') filtered = filtered.filter((row) => row.onShoppingList);
      return filtered.sort((a, b) => {
        if (a.scannedAt && b.scannedAt) {
          const d = b.scannedAt.getTime() - a.scannedAt.getTime();
          if (d !== 0) return d;
        } else if (a.scannedAt && !b.scannedAt) return -1;
        else if (!a.scannedAt && b.scannedAt) return 1;
        if (a.status === 'unknown' && b.status !== 'unknown') return -1;
        if (a.status !== 'unknown' && b.status === 'unknown') return 1;
        if (a.status === 'missing' && b.status === 'ok') return -1;
        if (a.status === 'ok' && b.status === 'missing') return 1;
        return 0;
      });
    },
    []
  );

  // --- TÁBLÁZAT ADATOK ---
  const tableData = useMemo(
    () => buildTableRows(supplierData, inventoryData, filterMode),
    [buildTableRows, supplierData, inventoryData, filterMode]
  );

  // (removed duplicate tableData body - using buildTableRows above)
  void (() => {
    const allEans = new Set([...Object.keys(supplierData), ...Object.keys(inventoryData)]);
    const rows = Array.from(allEans).map((ean) => {
      const suppItem = supplierData[ean];
      const invItem = inventoryData[ean];
      const name = suppItem?.name || 'Ismeretlen Termék';
      const netPrice = suppItem?.netPrice || 0; // Net purchase price
      const vatRate = suppItem?.vatRate || 27;
      // Calculate Gross purchase price from Net (for edit modal)
      const grossPurchasePrice =
        netPrice > 0 ? Math.round(netPrice * (1 + vatRate / 100) * 100) / 100 : 0;
      const sellingPrice = suppItem?.sellingPrice || 0; // Gross selling price
      const quantity = invItem?.quantity || 0;
      const minQuantity = invItem?.min_quantity;
      const onShoppingList = invItem?.on_shopping_list ?? false;
      const total = netPrice * quantity; // Use Net for total value calculation
      const scannedAt = invItem?.scannedAt;
      // NULL = no limit, 0 = valid limit
      const isLowStock = minQuantity != null && quantity <= minQuantity;
      // Low stock but on shopping list is considered "handled"
      const isLowStockUnhandled = isLowStock && !onShoppingList;

      // Status should be based on existence in inventory, not quantity.
      // If invItem exists (tracked in DB), it's "scanned" even when quantity is 0.
      let status: 'ok' | 'missing' | 'unknown' = 'missing';
      if (invItem) {
        status = suppItem ? 'ok' : 'unknown';
      } else {
        status = 'missing';
      }

      const unit = invItem?.unit || suppItem?.unit || 'db';

      return {
        ean,
        name,
        price: netPrice, // Keep as Net for display (backward compatibility)
        grossPurchasePrice, // Add Gross purchase price for edit modal
        sellingPrice,
        quantity,
        total,
        status,
        scannedAt,
        minQuantity: minQuantity ?? null,
        onShoppingList,
        isLowStock,
        isLowStockUnhandled,
        unit,
      };
    });

    // Server-side search is already applied, no need to filter here
    let filtered = rows;

    // Apply active filter (UI-specific views)
    if (filterMode === 'LOW_STOCK') {
      // Low Stock filter: only show items that are low stock AND NOT on shopping list (unhandled)
      filtered = filtered.filter((row) => row.isLowStockUnhandled);
    } else if (filterMode === 'SHOPPING_LIST') {
      filtered = filtered.filter((row) => row.onShoppingList);
    }

    return filtered.sort((a, b) => {
      // Első prioritás: scannedAt dátum (legújabbak elől)
      if (a.scannedAt && b.scannedAt) {
        const dateDiff = b.scannedAt.getTime() - a.scannedAt.getTime();
        if (dateDiff !== 0) return dateDiff;
      } else if (a.scannedAt && !b.scannedAt) {
        return -1; // a van, b nincs -> a előrébb
      } else if (!a.scannedAt && b.scannedAt) {
        return 1; // a nincs, b van -> b előrébb
      }
      // Ha mindkettőnek nincs scannedAt, vagy egyenlőek, akkor státusz alapján
      if (a.status === 'unknown' && b.status !== 'unknown') return -1;
      if (a.status !== 'unknown' && b.status === 'unknown') return 1;
      if (a.status === 'missing' && b.status === 'ok') return -1;
      if (a.status === 'ok' && b.status === 'missing') return 1;
      return 0;
    });
  })();

  // Items to print – use full list from printFullData when set (all pages), else current page
  const itemsToPrint = useMemo(() => {
    const sourceRows = printFullData
      ? buildTableRows(printFullData.supplierMap, printFullData.inventoryMap, filterMode)
      : tableData;
    if (filterMode === 'SHOPPING_LIST') {
      const shoppingListItems = sourceRows.filter((row) => row.onShoppingList);
      if (selectedEans.length > 0) {
        return shoppingListItems.filter((row) => selectedEans.includes(row.ean));
      }
      return shoppingListItems;
    }
    if (filterMode === 'LOW_STOCK') {
      if (selectedEans.length > 0) {
        return sourceRows.filter((row) => selectedEans.includes(row.ean));
      }
      return sourceRows;
    }
    return [];
  }, [tableData, filterMode, selectedEans, printFullData, buildTableRows]);

  // When printFullData is set, open print dialog then clear (so next print uses current page again)
  useEffect(() => {
    if (!printFullData) return;
    const t = setTimeout(() => {
      window.print();
      setPrintFullData(null);
    }, 300);
    return () => clearTimeout(t);
  }, [printFullData]);

  // Print: if more than one page and no selection, fetch all items first then print
  const handlePrintClick = () => {
    const needFullList =
      (filterMode === 'LOW_STOCK' || filterMode === 'SHOPPING_LIST') &&
      selectedEans.length === 0 &&
      totalItems > pageSize &&
      fetchAllItemsForPrint;
    if (needFullList) {
      setPrintingAll(true);
      fetchAllItemsForPrint()
        .then(({ supplierMap, inventoryMap }) => {
          setPrintFullData({ supplierMap, inventoryMap });
        })
        .catch((err) => {
          console.error('[Dashboard] fetchAllItemsForPrint failed:', err);
          toast.error('Teljes lista betöltése sikertelen, csak az aktuális oldal nyomtatódik.');
          window.print();
        })
        .finally(() => setPrintingAll(false));
    } else {
      window.print();
    }
  };

  // Calculate filter counts

  // Toggle shopping list status
  const handleToggleShoppingList = async (ean: string, currentStatus: boolean) => {
    setTogglingShoppingList(ean);
    const newStatus = !currentStatus;

    // Optimistic update: immediately update UI before API call
    // This ensures the item disappears from "Low Stock" filter instantly
    if (setInventoryData) {
      setInventoryData((prev) => {
        const existingItem = prev[ean];
        if (!existingItem) {
          // If item doesn't exist in inventory, we can't toggle it
          return prev;
        }
        // Create a new object to ensure React detects the change
        return {
          ...prev,
          [ean]: {
            ...existingItem,
            on_shopping_list: newStatus,
          },
        };
      });
    }

    try {
      const updatedItem = await supabaseService.toggleShoppingList(ean, newStatus);

      // Update local state with confirmed database values to ensure consistency
      // Use functional update to ensure React detects the change
      if (setInventoryData && updatedItem) {
        setInventoryData((prev) => {
          const existingItem = prev[ean];
          if (!existingItem) return prev;

          // Create a completely new object to force re-render
          return {
            ...prev,
            [ean]: {
              ean: ean,
              quantity: updatedItem.quantity ?? existingItem.quantity ?? 0,
              min_quantity: updatedItem.min_quantity ?? existingItem.min_quantity ?? null,
              on_shopping_list: updatedItem.on_shopping_list ?? newStatus, // Use DB value
              scannedAt: updatedItem.last_scanned_at
                ? new Date(updatedItem.last_scanned_at)
                : (existingItem.scannedAt ?? new Date()),
            },
          };
        });
      }

      toast.success(
        currentStatus ? 'Eltávolítva a bevásárlólistáról' : 'Hozzáadva a bevásárlólistához'
      );
    } catch (error: any) {
      // Rollback optimistic update on error
      if (setInventoryData) {
        setInventoryData((prev) => {
          const existingItem = prev[ean];
          if (!existingItem) return prev;
          return {
            ...prev,
            [ean]: {
              ...existingItem,
              on_shopping_list: currentStatus, // Revert to original status
            },
          };
        });
      }

      console.error('Error toggling shopping list:', error);
      const friendlyError = getUserFriendlyError(error);
      toast.error(friendlyError);
    } finally {
      setTogglingShoppingList(null);
    }
  };

  // --- KEZELŐK ---
  const handleDeleteClick = (ean: string) => {
    setDeleteConfirm({ ean });
  };

  // Excel Export for Shopping List or Kifogyóban
  const handleExportExcel = () => {
    if (!hasProcurementModule) {
      toast.error('A procurement modul nincs engedélyezve');
      return;
    }

    if (filterMode !== 'SHOPPING_LIST' && filterMode !== 'LOW_STOCK') {
      toast.error('Excel export csak a Bevásárlólista vagy Kifogyóban szűrőben érhető el');
      return;
    }

    const items =
      filterMode === 'SHOPPING_LIST' ? tableData.filter((row) => row.onShoppingList) : tableData;
    const toExport =
      selectedEans.length > 0 ? items.filter((row) => selectedEans.includes(row.ean)) : items;

    if (toExport.length === 0) {
      toast.error(
        filterMode === 'SHOPPING_LIST'
          ? 'Nincs exportálható tétel a bevásárlólistán'
          : 'Nincs exportálható tétel a kifogyóban listán'
      );
      return;
    }

    const itemsForExport = toExport.map((row) => {
      const suppItem = supplierData[row.ean];
      return {
        ean: row.ean,
        name: row.name,
        quantity: row.quantity,
        netPrice: suppItem?.netPrice || 0,
        sellingPrice: suppItem?.sellingPrice || 0,
      };
    });

    exportShoppingListToExcel(itemsForExport, supplierData);
    toast.success(`Excel exportálva: ${toExport.length} tétel`);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm) return;

    setIsDeleting(deleteConfirm.ean);
    setError(null); // Clear previous errors
    try {
      await onDelete(deleteConfirm.ean);
      toast.success('Tétel sikeresen törölve');
      setDeleteConfirm(null);
    } catch (error) {
      setError({
        error,
        context: { action: 'törlés', item: deleteConfirm.ean },
      });
    } finally {
      setIsDeleting(null);
    }
  };

  const handleEditClick = (row: any) => {
    const invItem = inventoryData[row.ean];
    const suppItem = supplierData[row.ean];
    setEditingItem({
      ean: row.ean,
      name: row.name,
      price: row.grossPurchasePrice || 0, // Use pre-calculated Gross purchase price
      sellingPrice: row.sellingPrice || 0, // Gross selling price
      // New hybrid pricing fields
      netPrice: suppItem?.netPrice || invItem?.netPrice,
      netSellingPrice: suppItem?.netSellingPrice || invItem?.netSellingPrice,
      vatRate: suppItem?.vatRate || invItem?.vatRate || 27,
      quantity: row.quantity,
      unit: invItem?.unit || suppItem?.unit || 'db',
      is_weighable: suppItem?.isWeighable || (suppItem as any)?.is_weighable || false,
      unit_weight: suppItem?.unitWeight || (suppItem as any)?.unit_weight || 0,
      min_quantity: invItem?.min_quantity ?? null,
    });
  };

  const saveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingItem) return;

    setIsUpdating(editingItem.ean);
    setError(null); // Clear previous errors
    try {
      // CRITICAL: Ensure netPrice is explicitly passed if it exists in state
      // The editItem function will prioritize netPrice over price (gross) if both are provided
      await onUpdate(editingItem.ean, {
        name: editingItem.name,
        price: Number(editingItem.price), // Gross purchase price (price column)
        selling_price: Math.round(Number(editingItem.sellingPrice || 0)), // Gross selling price (selling_price column, integer)
        // Net prices (net_price and net_selling_price columns)
        // Explicitly pass netPrice even if it might be undefined - editItem will handle it
        netPrice: editingItem.netPrice === undefined ? undefined : Math.round(editingItem.netPrice), // Purchase net is primary + integer
        netSellingPrice: editingItem.netSellingPrice, // This is the value user typed in Net Selling field
        vatRate: editingItem.vatRate ?? 27,
        quantity: Number(editingItem.quantity),
        unit: editingItem.unit,
        is_weighable: editingItem.is_weighable,
        unit_weight: editingItem.unit_weight,
        min_quantity: editingItem.min_quantity,
      });
      // ✅ SUCCESS: Toast handled by App.tsx handleUpdateItem
      setEditingItem(null);
    } catch (error) {
      setError({
        error,
        context: { action: 'mentés', item: editingItem.ean },
      });
    } finally {
      setIsUpdating(null);
    }
  };

  return (
    <>
      {/* MAIN UI - Hidden when printing */}
      <div className="print:hidden">
        <div
          className="max-w-7xl mx-auto p-4 md:p-8 space-y-8 relative min-h-screen"
          style={{ backgroundColor: 'var(--color-background, #f9fafb)' }}
        >
          {/* Error Display */}
          {error && (
            <div className="mb-4">
              <ErrorDisplay
                error={error.error}
                context={error.context}
                onRetry={() => {
                  if (error.context.action === 'törlés' && deleteConfirm) {
                    handleDeleteConfirm();
                  } else if (error.context.action === 'mentés' && editingItem) {
                    // Retry save by submitting the form again
                    const form = document.querySelector('#edit-modal form') as HTMLFormElement;
                    if (form) {
                      const submitEvent = new Event('submit', { bubbles: true, cancelable: true });
                      form.dispatchEvent(submitEvent);
                    }
                  }
                }}
                onDismiss={() => setError(null)}
              />
            </div>
          )}

          {/* --- TÖRLÉS MEGERŐSÍTÉS DIALOG --- */}
          <ConfirmDialog
            isOpen={!!deleteConfirm}
            title="Tétel törlése"
            message={`Biztosan törölni szeretnéd a következő tételt? EAN: ${deleteConfirm?.ean || ''}`}
            onConfirm={handleDeleteConfirm}
            onCancel={() => setDeleteConfirm(null)}
            confirmText="Törlés"
            cancelText="Mégse"
            variant="danger"
          />

          {/* --- SZERKESZTŐ MODAL (Ablak) --- */}
          {editingItem && (
            <div
              className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
              id="edit-modal"
            >
              <div className="bg-card rounded-xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col animate-in fade-in zoom-in duration-200">
                {/* Header - Sticky */}
                <div className="bg-primary p-4 flex justify-between items-center text-white flex-shrink-0">
                  <h3 className="font-bold flex items-center gap-2">
                    <Pencil size={18} /> Tétel Javítása
                  </h3>
                  <button
                    onClick={() => setEditingItem(null)}
                    className="hover:bg-primary/90 p-1 rounded"
                    aria-label="Bezárás"
                  >
                    <X size={20} />
                  </button>
                </div>

                {/* Scrollable Content */}
                <div className="overflow-y-auto flex-1">
                  <form onSubmit={saveEdit} className="p-6 space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-500 dark:text-gray-400 uppercase mb-1">
                        EAN (Nem módosítható)
                      </label>
                      <input
                        type="text"
                        value={editingItem.ean}
                        disabled
                        className="w-full p-2 bg-slate-100 dark:bg-gray-700 border dark:border-gray-600 rounded text-slate-500 dark:text-gray-400 font-mono"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-500 dark:text-gray-400 uppercase mb-1">
                        Termék Neve
                      </label>
                      <input
                        type="text"
                        value={editingItem.name}
                        onChange={(e) => setEditingItem({ ...editingItem, name: e.target.value })}
                        className="w-full p-2 border border-slate-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-accent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-500 dark:text-gray-400 uppercase mb-1">
                        Mértékegység
                      </label>
                      <select
                        value={editingItem.unit}
                        onChange={(e) =>
                          setEditingItem({
                            ...editingItem,
                            unit: e.target.value as 'db' | 'm' | 'kg',
                          })
                        }
                        className="w-full p-2 border border-slate-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-accent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="db">db</option>
                        <option value="m">m</option>
                        <option value="kg">kg</option>
                      </select>
                    </div>

                    {/* Hybrid Pricing Input - SZINKRONIZÁLT VERZIÓ */}
                    <PricingInput
                      purchaseNet={editingItem.netPrice}
                      purchaseGross={editingItem.price}
                      sellingNet={editingItem.netSellingPrice}
                      sellingGross={editingItem.sellingPrice}
                      vatRate={editingItem.vatRate ?? 27}
                      // 1. HA A NETTÓT ÍROD -> Számolja a Bruttót is!
                      onPurchaseNetChange={(val) => {
                        const roundedNet = Math.round(val);
                        const vat = editingItem.vatRate ?? 27;
                        const gross =
                          roundedNet > 0 ? Math.round(roundedNet * (1 + vat / 100) * 100) / 100 : 0;
                        setEditingItem({ ...editingItem, netPrice: roundedNet, price: gross });
                      }}
                      // 2. HA A BRUTTÓT ÍROD -> Számolja a Nettót is!
                      onPurchaseGrossChange={(val) => {
                        const vat = editingItem.vatRate ?? 27;
                        const net = val > 0 ? Math.round((val / (1 + vat / 100)) * 100) / 100 : 0;
                        setEditingItem({ ...editingItem, price: val, netPrice: net });
                      }}
                      // 3. ELADÁSI ÁR SZINKRON
                      onSellingNetChange={(val) => {
                        const vat = editingItem.vatRate ?? 27;
                        const gross = val > 0 ? Math.round(val * (1 + vat / 100)) : 0;
                        setEditingItem({
                          ...editingItem,
                          netSellingPrice: val,
                          sellingPrice: gross,
                        });
                      }}
                      onSellingGrossChange={(val) => {
                        const roundedGross = Math.round(val);
                        const vat = editingItem.vatRate ?? 27;
                        const net =
                          roundedGross > 0
                            ? Math.round((roundedGross / (1 + vat / 100)) * 100) / 100
                            : 0;
                        setEditingItem({
                          ...editingItem,
                          sellingPrice: roundedGross,
                          netSellingPrice: net,
                        });
                      }}
                      // 4. ÁFA VÁLTÁS -> Újraszámolja a Bruttókat (Nettó marad fix)
                      onVatRateChange={(val) => {
                        const newGrossBuy = editingItem.netPrice
                          ? Math.round(editingItem.netPrice * (1 + val / 100) * 100) / 100
                          : 0;
                        const newGrossSell = editingItem.netSellingPrice
                          ? Math.round(editingItem.netSellingPrice * (1 + val / 100) * 100) / 100
                          : 0;
                        setEditingItem({
                          ...editingItem,
                          vatRate: val,
                          price: newGrossBuy,
                          sellingPrice: newGrossSell,
                        });
                      }}
                      purchaseLabel="Beszerzési Ár"
                      sellingLabel="Eladási Ár"
                    />

                    <div>
                      <label className="block text-xs font-bold text-slate-500 dark:text-gray-400 uppercase mb-1 flex items-center gap-1">
                        <Lock size={14} />
                        Mennyiség ({editingItem.unit})
                      </label>
                      <input
                        type="number"
                        step={editingItem.unit === 'm' || editingItem.unit === 'kg' ? '0.01' : '1'}
                        value={editingItem.quantity}
                        disabled
                        className="w-full p-2 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-gray-400 cursor-not-allowed border border-slate-300 dark:border-gray-600 rounded font-bold"
                      />
                      <p className="text-xs text-slate-500 dark:text-gray-400 mt-1">
                        A mennyiség csak leltározással vagy selejtezéssel módosítható a naplózás
                        érdekében.
                      </p>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-500 dark:text-gray-400 uppercase mb-1 flex items-center gap-1">
                        <Bell size={14} />
                        Jelzés limit ({editingItem.unit})
                      </label>
                      <input
                        type="number"
                        min="0"
                        step={editingItem.unit === 'm' || editingItem.unit === 'kg' ? '0.001' : '1'}
                        value={editingItem.min_quantity === null ? '' : editingItem.min_quantity}
                        onChange={(e) => {
                          const raw = e.target.value;
                          // Empty = null (no limit), 0 = valid limit
                          if (raw === '') {
                            setEditingItem({ ...editingItem, min_quantity: null });
                          } else {
                            const val = parseFloat(raw.replace(',', '.'));
                            setEditingItem({
                              ...editingItem,
                              min_quantity: isNaN(val) || val < 0 ? null : val,
                            });
                          }
                        }}
                        onFocus={(e) => e.target.select()}
                        className="w-full p-2 border border-slate-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-accent outline-none bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        placeholder="Nincs limit"
                      />
                    </div>

                    <div className="pt-4 flex gap-3">
                      <button
                        type="button"
                        onClick={() => setEditingItem(null)}
                        className="flex-1 py-2 text-slate-600 dark:text-gray-300 hover:bg-slate-100 dark:hover:bg-gray-700 rounded-lg"
                      >
                        Mégse
                      </button>
                      <button
                        type="submit"
                        disabled={isUpdating === editingItem.ean}
                        className="flex-1 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 font-bold flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isUpdating === editingItem.ean ? (
                          <>
                            <Loader2 className="animate-spin" size={18} /> Mentés...
                          </>
                        ) : (
                          <>
                            <Save size={18} /> Mentés
                          </>
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* FEJLÉC ÉS KÁRTYÁK (Változatlan) */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 dark:text-gray-100 print:hidden">
                Leltár Áttekintés
              </h1>
              <p className="text-slate-500 dark:text-gray-400 text-sm print:hidden">
                Valós idejű helyzetkép a raktárról
              </p>
            </div>
            <div className="flex gap-3 print:hidden">
              <button
                onClick={() => setView(AppView.RECONCILIATION)}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 transition font-medium"
              >
                <Sparkles size={18} /> Részletes Elemzés
              </button>
              <button
                onClick={() => setView(AppView.INVENTORY_SCAN)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium shadow-md"
              >
                Folytatás (Szkennelés)
              </button>
            </div>
          </div>

          {/* FILTER BUTTONS */}
          <div className="flex gap-3 flex-wrap print:hidden">
            <button
              onClick={() => setFilterMode('ALL')}
              className={`px-4 py-2 rounded-lg font-medium transition ${
                filterMode === 'ALL'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              Összes
            </button>
            {hasProcurementModule && (
              <>
                <button
                  onClick={() => setFilterMode('LOW_STOCK')}
                  className={`px-4 py-2 rounded-lg font-medium transition relative ${
                    filterMode === 'LOW_STOCK'
                      ? 'bg-red-600 text-white shadow-md'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <AlertTriangle size={18} />
                    Kifogyóban
                    {(globalStats?.low_stock_count || 0) > 0 && (
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                          filterMode === 'LOW_STOCK' ? 'bg-red-700' : 'bg-red-500 text-white'
                        }`}
                      >
                        {globalStats?.low_stock_count}
                      </span>
                    )}
                  </div>
                </button>
                <button
                  onClick={() => setFilterMode('SHOPPING_LIST')}
                  className={`px-4 py-2 rounded-lg font-medium transition relative ${
                    filterMode === 'SHOPPING_LIST'
                      ? 'bg-green-600 text-white shadow-md'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <ShoppingCart size={18} />
                    Bevásárlólista
                    {(globalStats?.shopping_list_count || 0) > 0 && (
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                          filterMode === 'SHOPPING_LIST'
                            ? 'bg-green-700'
                            : 'bg-green-500 text-white'
                        }`}
                      >
                        {globalStats?.shopping_list_count}
                      </span>
                    )}
                  </div>
                </button>
              </>
            )}
            {hasProcurementModule &&
              (filterMode === 'SHOPPING_LIST' || filterMode === 'LOW_STOCK') && (
                <>
                  <button
                    onClick={handlePrintClick}
                    disabled={printingAll}
                    className={`px-4 py-2 rounded-lg font-medium transition shadow-md flex items-center gap-2 print:hidden ${
                      filterMode === 'LOW_STOCK'
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    } ${printingAll ? 'opacity-70 cursor-wait' : ''}`}
                    title={
                      selectedEans.length > 0
                        ? `Kijelöltek nyomtatása (${selectedEans.length} db)`
                        : totalItems > pageSize
                          ? `Összes tétel nyomtatása (${totalItems} db)`
                          : 'Lista nyomtatása'
                    }
                  >
                    {printingAll ? (
                      <Loader2 size={18} className="animate-spin" />
                    ) : (
                      <Printer size={18} />
                    )}
                    {printingAll
                      ? 'Betöltés...'
                      : selectedEans.length > 0
                        ? `Kijelöltek nyomtatása (${selectedEans.length} db)`
                        : 'Lista nyomtatása'}
                  </button>
                  <button
                    onClick={handleExportExcel}
                    className={`px-4 py-2 rounded-lg font-medium transition shadow-md flex items-center gap-2 print:hidden ${
                      filterMode === 'LOW_STOCK'
                        ? 'bg-red-600 text-white hover:bg-red-700'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                    title="Excel export"
                  >
                    <Printer size={18} />
                    Excel Export
                  </button>
                </>
              )}
          </div>

          <div
            className={`grid grid-cols-1 md:grid-cols-2 ${isAdmin ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-6 print:hidden`}
          >
            {/* Készlet Értéke - Only visible to admins */}
            {isAdmin && (
              <div className="bg-card p-6 rounded-xl shadow-sm">
                <p className="text-xs font-bold text-slate-400 dark:text-gray-400 uppercase tracking-wider mb-1">
                  Készlet Értéke
                </p>
                <p className="text-3xl font-bold text-slate-800 dark:text-gray-100">
                  {globalStats ? globalStats.stock_value.toLocaleString() : '-'} Ft
                </p>
              </div>
            )}
            {/* 1. Megszámolt darab */}
            <div className="bg-card p-6 rounded-xl shadow-sm">
              <p className="text-xs font-bold text-slate-400 dark:text-gray-400 uppercase tracking-wider mb-1">
                Megszámolt Darab
              </p>
              <p className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                {globalStats ? globalStats.counted_items : '-'} db
              </p>
            </div>
            {/* 2. Megszámolt tétel */}
            <div className="bg-card p-6 rounded-xl shadow-sm">
              <p className="text-xs font-bold text-slate-400 dark:text-gray-400 uppercase tracking-wider mb-1">
                Megszámolt Tétel
              </p>
              <div className="flex items-center gap-2">
                <p className="text-3xl font-bold text-slate-800 dark:text-gray-100">
                  {globalStats ? globalStats.total_items : '-'}
                </p>
                <span className="text-sm text-slate-400 dark:text-gray-400">tétel</span>
              </div>
            </div>
            {/* 3. Ár nélküli tételek - kattintható szűrő */}
            <div
              onClick={() => setFilterMode(filterMode === 'NO_PRICE' ? 'ALL' : 'NO_PRICE')}
              className={`p-6 rounded-xl shadow-sm cursor-pointer transition hover:ring-2 hover:ring-amber-400 ${
                filterMode === 'NO_PRICE'
                  ? 'bg-amber-100 dark:bg-amber-900/40 ring-2 ring-amber-500'
                  : globalStats && globalStats.unknown > 0
                    ? 'bg-amber-50 dark:bg-amber-900/20'
                    : 'bg-card'
              }`}
            >
              <p
                className={`text-xs font-bold uppercase tracking-wider mb-1 ${(globalStats && globalStats.unknown > 0) || filterMode === 'NO_PRICE' ? 'text-amber-600 dark:text-amber-400' : 'text-slate-400 dark:text-gray-400'}`}
              >
                Ár Nélküli Tételek
              </p>
              <p
                className={`text-3xl font-bold ${(globalStats && globalStats.unknown > 0) || filterMode === 'NO_PRICE' ? 'text-amber-600 dark:text-amber-400' : 'text-slate-800 dark:text-gray-100'}`}
              >
                {globalStats ? globalStats.unknown : '-'}
              </p>
            </div>
            {/* 4. Még nincs beolvasva */}
            <div className="bg-card p-6 rounded-xl shadow-sm">
              <p className="text-xs font-bold text-slate-400 dark:text-gray-400 uppercase tracking-wider mb-1">
                Még Nincs Beolvasva
              </p>
              <div className="flex items-center gap-2">
                <p className="text-3xl font-bold text-slate-800 dark:text-gray-100">
                  {globalStats ? globalStats.not_scanned : '-'}
                </p>
                <span className="text-sm text-slate-400 dark:text-gray-400">tétel</span>
              </div>
            </div>
          </div>

          {/* TÁBLÁZAT */}
          <div className="bg-card rounded-xl shadow-sm overflow-hidden">
            <div className="p-4 border-b border-slate-100 dark:border-gray-700 bg-slate-50/50 dark:bg-gray-900/50 flex flex-col md:flex-row gap-4 justify-between items-center print:hidden">
              <div className="relative w-full md:w-96">
                <Search
                  className="absolute left-3 top-2.5 text-slate-400 dark:text-gray-500"
                  size={18}
                />
                <input
                  type="text"
                  id="dashboard-search-input"
                  placeholder="Keresés név vagy EAN alapján... (Ctrl+K)"
                  className="w-full pl-10 pr-4 py-2 border border-slate-200 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-accent outline-none text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="text-xs text-slate-400 dark:text-gray-400">
                Összesen {totalItems} tétel • Oldal: {currentPage} /{' '}
                {Math.ceil(totalItems / pageSize) || 1}
              </div>
            </div>
          </div>

          {/* MOBILE: Card View */}
          <div className="md:hidden print:hidden space-y-3 p-4">
            {tableData.length === 0 ? (
              <div className="p-8 text-center text-slate-400">Nincs megjeleníthető adat.</div>
            ) : (
              tableData.map((row) => {
                const isSelectionActive = filterMode === 'SHOPPING_LIST' && selectedEans.length > 0;
                const isSelected = selectedEans.includes(row.ean);
                const shouldHideInPrint = isSelectionActive && !isSelected;

                return (
                  <div
                    key={row.ean}
                    className={`bg-white border border-slate-200 rounded-lg p-4 space-y-3 shadow-sm ${shouldHideInPrint ? 'print:hidden' : ''}`}
                  >
                    {/* Header: Name and Actions */}
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-slate-800 truncate">{row.name}</h3>
                          {row.onShoppingList && (
                            <div title="Bevásárlólistán">
                              <ShoppingCart
                                size={16}
                                className="text-green-600 dark:text-green-400 shrink-0"
                              />
                            </div>
                          )}
                        </div>
                        <p className="text-xs font-mono text-slate-500 mt-1">{row.ean}</p>
                      </div>
                      <div className="flex gap-2 shrink-0">
                        {/* In "All" view: Show Printer icon first for label printing */}
                        {filterMode === 'ALL' && addToPrintQueue && (
                          <button
                            onClick={() => addToPrintQueue(row.ean)}
                            className="p-1.5 text-blue-600 hover:bg-blue-100 rounded transition"
                            title="Címke nyomtatási sorhoz adás"
                          >
                            <Printer size={16} />
                          </button>
                        )}
                        {/* Shopping Cart icon only for procurement module views (low_stock or shopping_list) */}
                        {hasProcurementModule && filterMode !== 'ALL' && (
                          <button
                            onClick={() => handleToggleShoppingList(row.ean, row.onShoppingList)}
                            disabled={togglingShoppingList === row.ean}
                            className={`p-1.5 rounded transition disabled:opacity-50 disabled:cursor-not-allowed ${
                              row.onShoppingList
                                ? 'text-red-600 hover:bg-red-100'
                                : 'text-green-600 hover:bg-green-100'
                            }`}
                            title={
                              row.onShoppingList
                                ? 'Eltávolítás a bevásárlólistáról'
                                : 'Hozzáadás a bevásárlólistához'
                            }
                          >
                            {togglingShoppingList === row.ean ? (
                              <Loader2 className="animate-spin" size={16} />
                            ) : (
                              <ShoppingCart size={16} />
                            )}
                          </button>
                        )}
                        {/* Printer icon for shopping list items in shopping_list view */}
                        {filterMode === 'SHOPPING_LIST' && row.onShoppingList && (
                          <button
                            onClick={() => window.print()}
                            className="p-1.5 text-green-600 hover:bg-green-100 rounded transition"
                            title="Nyomtatás"
                          >
                            <Printer size={16} />
                          </button>
                        )}
                        {/* Edit and Delete buttons only visible in "All" view */}
                        {filterMode === 'ALL' && (
                          <>
                            <button
                              onClick={() => handleEditClick(row)}
                              className="p-1.5 text-blue-600 hover:bg-blue-100 rounded transition"
                              title="Javítás"
                            >
                              <Pencil size={16} />
                            </button>
                            <button
                              onClick={() => handleDeleteClick(row.ean)}
                              className="p-1.5 text-red-400 hover:bg-red-100 hover:text-red-600 rounded transition"
                              title="Törlés"
                            >
                              <Trash2 size={16} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div>
                      {row.status === 'ok' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                          <CheckCircle size={12} /> Beolvasva
                        </span>
                      ) : row.status === 'missing' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
                          <PackageX size={12} /> Nincs beolvasva
                        </span>
                      ) : row.status === 'unknown' ? (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                          <AlertCircle size={12} /> Ismeretlen
                        </span>
                      ) : null}
                    </div>

                    {/* Details Grid */}
                    <div className="grid grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                      <div>
                        <p className="text-xs text-slate-500 uppercase font-bold mb-1">Egységár</p>
                        <p className="text-sm font-medium text-slate-600">
                          {row.price.toLocaleString()} Ft
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase font-bold mb-1">Mennyiség</p>
                        <div className="flex items-center gap-1">
                          {/*
                           * Show "-" ONLY if item is missing (exists only in supplier).
                           * If it's tracked in inventory (ok/unknown), show 0 as "0 db".
                           */}
                          {(() => {
                            const isMissing = row.status === 'missing';
                            const unit = row.unit || 'db';
                            const displayQty = isMissing ? '-' : `${row.quantity} ${unit}`;
                            const isZeroTracked = !isMissing && row.quantity === 0;

                            if (row.isLowStockUnhandled) {
                              return (
                                <>
                                  <AlertTriangle
                                    size={14}
                                    className="text-red-600 dark:text-red-400"
                                  />
                                  <p className="text-sm font-bold text-red-600 dark:text-red-400">
                                    {displayQty}
                                  </p>
                                </>
                              );
                            }

                            return (
                              <p
                                className={`text-sm font-bold ${
                                  isMissing
                                    ? 'text-slate-400'
                                    : isZeroTracked
                                      ? 'text-slate-600'
                                      : 'text-slate-800'
                                }`}
                              >
                                {displayQty}
                              </p>
                            );
                          })()}
                        </div>
                      </div>
                      <div className="col-span-2">
                        <p className="text-xs text-slate-500 uppercase font-bold mb-1">Összérték</p>
                        <p className="text-base font-medium text-slate-800">
                          {row.total > 0 ? `${row.total.toLocaleString()} Ft` : '-'}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* DESKTOP: Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left text-sm print:text-xs">
              <thead className="bg-slate-50 dark:bg-gray-700 text-slate-500 dark:text-gray-300 font-medium uppercase text-xs border-b border-gray-200 dark:border-gray-600 print:bg-gray-100">
                <tr>
                  {filterMode === 'SHOPPING_LIST' && (
                    <th className="px-6 py-3 print:hidden">
                      <input
                        type="checkbox"
                        checked={
                          tableData.length > 0 &&
                          tableData.every((row) => selectedEans.includes(row.ean))
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedEans(tableData.map((row) => row.ean));
                          } else {
                            setSelectedEans([]);
                          }
                        }}
                        className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2 cursor-pointer"
                        title="Összes kijelölése"
                      />
                    </th>
                  )}
                  <th className="px-6 py-3 print:hidden">Művelet</th>
                  <th className="px-6 py-3 print:hidden">Státusz</th>
                  <th className="px-6 py-3">Termék Neve</th>
                  <th className="px-6 py-3">EAN / Vonalkód</th>
                  <th className="px-6 py-3 text-right">Beszerzési Ár</th>
                  {filterMode === 'SHOPPING_LIST' && (
                    <th className="px-6 py-3 text-right">Eladási Ár</th>
                  )}
                  <th className="px-6 py-3 text-right">Jelenlegi Készlet</th>
                  <th className="px-6 py-3 text-right print:hidden">Összérték</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {tableData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={filterMode === 'SHOPPING_LIST' ? 9 : 7}
                      className="px-6 py-12 text-center"
                    >
                      <div className="flex flex-col items-center gap-4">
                        <div className="text-slate-400 dark:text-gray-500 text-lg">
                          Nincs még leltározott termék.
                        </div>
                        <div className="text-slate-500 dark:text-gray-600 text-sm">
                          Kezdj el leltározni a "Leltározás" menüpontban!
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  tableData.map((row) => {
                    const isSelectionActive =
                      filterMode === 'SHOPPING_LIST' && selectedEans.length > 0;
                    const isSelected = selectedEans.includes(row.ean);
                    const shouldHideInPrint = isSelectionActive && !isSelected;

                    return (
                      <tr
                        key={row.ean}
                        className={`bg-card even:bg-gray-50 dark:even:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition group ${shouldHideInPrint ? 'print:hidden' : ''}`}
                      >
                        {filterMode === 'SHOPPING_LIST' && (
                          <td className="px-6 py-3 print:hidden">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedEans([...selectedEans, row.ean]);
                                } else {
                                  setSelectedEans(selectedEans.filter((ean) => ean !== row.ean));
                                }
                              }}
                              className="w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 focus:ring-2 cursor-pointer"
                              title={isSelected ? 'Kijelölés megszüntetése' : 'Kijelölés'}
                            />
                          </td>
                        )}
                        <td className="px-6 py-3 print:hidden">
                          <div className="flex gap-2">
                            {/* In "All" view: Show Printer icon first for label printing */}
                            {filterMode === 'ALL' && addToPrintQueue && (
                              <button
                                onClick={() => addToPrintQueue(row.ean)}
                                className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded transition"
                                title="Címke nyomtatási sorhoz adás"
                              >
                                <Printer size={16} />
                              </button>
                            )}
                            {/* Shopping Cart icon only for procurement module views (low_stock or shopping_list) */}
                            {hasProcurementModule && filterMode !== 'ALL' && (
                              <button
                                onClick={() =>
                                  handleToggleShoppingList(row.ean, row.onShoppingList)
                                }
                                disabled={togglingShoppingList === row.ean}
                                className={`p-1.5 rounded transition disabled:opacity-50 disabled:cursor-not-allowed ${
                                  row.onShoppingList
                                    ? 'text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30'
                                    : 'text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30'
                                }`}
                                title={
                                  row.onShoppingList
                                    ? 'Eltávolítás a bevásárlólistáról'
                                    : 'Hozzáadás a bevásárlólistához'
                                }
                              >
                                {togglingShoppingList === row.ean ? (
                                  <Loader2 className="animate-spin" size={16} />
                                ) : (
                                  <ShoppingCart size={16} />
                                )}
                              </button>
                            )}
                            {/* Printer icon for shopping list items in shopping_list view */}
                            {filterMode === 'SHOPPING_LIST' && row.onShoppingList && (
                              <button
                                onClick={() => window.print()}
                                className="p-1.5 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/30 rounded transition"
                                title="Nyomtatás"
                              >
                                <Printer size={16} />
                              </button>
                            )}
                            {/* Edit and Delete buttons only visible in "All" view */}
                            {filterMode === 'ALL' && (
                              <>
                                <button
                                  onClick={() => handleEditClick(row)}
                                  className="p-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded transition"
                                  title="Javítás"
                                >
                                  <Pencil size={16} />
                                </button>
                                <button
                                  onClick={() => handleDeleteClick(row.ean)}
                                  disabled={isDeleting === row.ean}
                                  className="p-1.5 text-red-400 dark:text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 hover:text-red-600 dark:hover:text-red-400 rounded transition disabled:opacity-50 disabled:cursor-not-allowed"
                                  title="Törlés"
                                >
                                  {isDeleting === row.ean ? (
                                    <Loader2 className="animate-spin" size={16} />
                                  ) : (
                                    <Trash2 size={16} />
                                  )}
                                </button>
                              </>
                            )}
                          </div>
                        </td>

                        <td className="px-6 py-3 print:hidden">
                          {row.status === 'ok' ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                              <CheckCircle size={12} /> Beolvasva
                            </span>
                          ) : row.status === 'missing' ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 dark:bg-gray-700 text-slate-600 dark:text-gray-300">
                              <PackageX size={12} /> Nincs beolvasva
                            </span>
                          ) : row.status === 'unknown' ? (
                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400">
                              <AlertCircle size={12} /> Ismeretlen
                            </span>
                          ) : null}
                        </td>
                        <td className="px-6 py-3 font-medium text-slate-800 dark:text-gray-100 print:text-gray-900">
                          {row.name}
                        </td>
                        <td className="px-6 py-3 font-mono text-slate-500 dark:text-gray-400 print:text-gray-700">
                          {row.ean}
                        </td>
                        <td className="px-6 py-3 text-right text-slate-600 dark:text-gray-300 print:text-gray-900">
                          {row.price.toLocaleString()} Ft
                        </td>
                        {filterMode === 'SHOPPING_LIST' && (
                          <td className="px-6 py-3 text-right text-slate-600 dark:text-gray-300 print:text-gray-900">
                            {row.sellingPrice.toLocaleString()} Ft
                          </td>
                        )}
                        <td className="px-6 py-3 text-right text-slate-600 dark:text-gray-300 print:text-gray-900">
                          <div className="flex items-center justify-end gap-1">
                            {/*
                             * Show "-" ONLY if item is missing (exists only in supplier).
                             * If it's tracked in inventory (ok/unknown), show number + unit (same style as Beszerzési ár).
                             */}
                            {(() => {
                              const isMissing = row.status === 'missing';
                              const unit = row.unit || 'db';
                              const displayText = isMissing ? '-' : `${row.quantity} ${unit}`;

                              if (row.isLowStockUnhandled) {
                                return (
                                  <>
                                    <AlertTriangle
                                      size={14}
                                      className="text-red-600 dark:text-red-400 print:hidden shrink-0"
                                    />
                                    <span className="text-red-600 dark:text-red-400 print:text-gray-900">
                                      {displayText}
                                    </span>
                                  </>
                                );
                              }

                              return (
                                <span
                                  className={
                                    isMissing
                                      ? 'text-slate-400 dark:text-gray-500 print:text-gray-500'
                                      : 'text-slate-600 dark:text-gray-300 print:text-gray-900'
                                  }
                                >
                                  {displayText}
                                </span>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="px-6 py-3 text-right font-medium text-slate-800 dark:text-gray-100 print:hidden">
                          {row.total > 0 ? `${row.total.toLocaleString()} Ft` : '-'}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* RECENT ACTIVITY WIDGET */}
          <div className="bg-card rounded-xl shadow-sm overflow-hidden print:hidden">
            <div className="p-4 border-b border-slate-100 dark:border-gray-700 bg-slate-50/50 dark:bg-gray-900/50 flex items-center gap-2">
              <Activity size={18} className="text-blue-600 dark:text-blue-400" />
              <h3 className="font-semibold text-slate-800 dark:text-gray-200">
                Legutóbbi Mozgások
              </h3>
            </div>
            <div className="divide-y divide-slate-100 dark:divide-gray-700">
              {loadingActivity ? (
                <div className="p-8 text-center text-slate-400 dark:text-gray-500">
                  <Loader2 className="animate-spin mx-auto mb-2" size={24} />
                  <p className="text-sm">Betöltés...</p>
                </div>
              ) : recentActivity.length === 0 ? (
                <div className="p-8 text-center text-slate-400 dark:text-gray-500">
                  <p className="text-sm">Még nincs tevékenység</p>
                </div>
              ) : (
                recentActivity.map((activity) => {
                  const time = new Date(activity.created_at).toLocaleTimeString('hu-HU', {
                    hour: '2-digit',
                    minute: '2-digit',
                  });

                  // Determine badge color based on action type
                  const getActionBadgeColor = (actionType: string) => {
                    switch (actionType) {
                      case 'SALE':
                        return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border-red-300 dark:border-red-800';
                      case 'COUNT':
                        return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-300 dark:border-green-800';
                      case 'RETURN':
                        return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-300 dark:border-purple-800';
                      case 'SCRAP':
                        return 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 border-orange-300 dark:border-orange-800';
                      case 'INTERNAL':
                        return 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400 border-purple-300 dark:border-purple-800';
                      default:
                        return 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600';
                    }
                  };

                  const getActionLabel = (actionType: string) => {
                    switch (actionType) {
                      case 'SALE':
                        return 'ELADÁS';
                      case 'COUNT':
                        return 'LELTÁR';
                      case 'RETURN':
                        return 'VISSZÁRU';
                      case 'SCRAP':
                        return 'SELEJT';
                      case 'INTERNAL':
                        return 'SAJÁT FELH.';
                      default:
                        return actionType;
                    }
                  };

                  return (
                    <div
                      key={activity.id}
                      className="px-4 md:px-6 py-3 hover:bg-slate-50 dark:hover:bg-gray-700/50 transition"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-mono text-slate-500 dark:text-gray-400 flex items-center gap-1">
                              <Clock size={12} />
                              {time}
                            </span>
                            <span
                              className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold border ${getActionBadgeColor(activity.action_type)}`}
                            >
                              {getActionLabel(activity.action_type)}
                            </span>
                          </div>
                          <p className="text-sm text-slate-700 dark:text-gray-300">
                            {activity.details}
                          </p>
                          <p className="text-xs text-slate-500 dark:text-gray-400 mt-1">
                            {activity.user_name}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Pagination Controls */}
          {totalItems > pageSize && (
            <div className="mt-6 flex items-center justify-between px-6 py-4 bg-card rounded-lg print:hidden">
              <div className="text-sm text-slate-600 dark:text-gray-400">
                Megjelenítve: {(currentPage - 1) * pageSize + 1} -{' '}
                {Math.min(currentPage * pageSize, totalItems)} / {totalItems}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={goToPrevPage}
                  disabled={currentPage === 1}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    currentPage === 1
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600'
                  }`}
                >
                  Előző
                </button>
                <span className="text-sm text-slate-600 dark:text-gray-400 font-medium">
                  Oldal {currentPage} / {Math.ceil(totalItems / pageSize)}
                </span>
                <button
                  onClick={goToNextPage}
                  disabled={currentPage >= Math.ceil(totalItems / pageSize)}
                  className={`px-4 py-2 rounded-lg font-medium transition ${
                    currentPage >= Math.ceil(totalItems / pageSize)
                      ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                      : 'bg-blue-600 dark:bg-blue-500 text-white hover:bg-blue-700 dark:hover:bg-blue-600'
                  }`}
                >
                  Következő
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* PRINT-ONLY SECTION - Only visible when printing */}
      {hasProcurementModule &&
        (filterMode === 'SHOPPING_LIST' || filterMode === 'LOW_STOCK') &&
        itemsToPrint.length > 0 && (
          <PrintableShoppingList
            title={filterMode === 'LOW_STOCK' ? 'Kifogyóban' : 'Bevásárlólista'}
            items={itemsToPrint.map((row) => ({
              ean: row.ean,
              name: row.name,
              quantity: row.quantity,
            }))}
            supplierData={printFullData?.supplierMap ?? supplierData}
            selectedCount={selectedEans.length}
          />
        )}
    </>
  );
};

export default Dashboard;
